// Helpers for selection-scoped Writing Assistant edits.
//
// The editor content lives in React state as an HTML string, but the user's
// selection arrives as plain text (window.getSelection().toString()). A naive
// string replace fails whenever the selection spans an inline tag like
// <strong> or crosses a paragraph boundary — so we map the plain-text
// selection onto the HTML's text nodes and splice with a DOM Range. This
// keeps every surrounding tag (links, lists, headings, formatting) intact.

const BLOCK_TAGS = /^(P|DIV|LI|UL|OL|H[1-6]|BLOCKQUOTE|TABLE|TR|TD)$/i;

// Strip ALL whitespace for matching — selection text and HTML text nodes
// disagree about whitespace at tag boundaries (newlines between <p>s,
// collapsed spaces), so matching on the whitespace-free text sidesteps
// every boundary case.
const squash = (s = "") => s.replace(/\s+/g, "");

/**
 * Replace the (plain-text) selected span inside an HTML string with new HTML.
 * Returns the updated HTML string, or null when the selection can't be
 * located — callers must treat null as "do not touch the document".
 */
export const replaceSelectionInHtml = (html, selectedText, replacementHtml) => {
  const target = squash(selectedText);
  if (!target) return null;

  const container = document.createElement("div");
  container.innerHTML = html;

  // Collect text nodes and build a whitespace-free string with a map from
  // each squashed char back to its (node, offset) position.
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  const map = []; // map[i] = { node, offset } of i-th non-whitespace char
  let squashed = "";
  let node;
  while ((node = walker.nextNode())) {
    const value = node.nodeValue || "";
    for (let i = 0; i < value.length; i++) {
      if (!/\s/.test(value[i])) {
        squashed += value[i];
        map.push({ node, offset: i });
      }
    }
  }

  let idx = squashed.indexOf(target);
  if (idx === -1) idx = squashed.toLowerCase().indexOf(target.toLowerCase());
  if (idx === -1) return null;

  const startPos = map[idx];
  const endPos = map[idx + target.length - 1];
  if (!startPos || !endPos) return null;

  const range = document.createRange();
  range.setStart(startPos.node, startPos.offset);
  range.setEnd(endPos.node, endPos.offset + 1);

  let replacement = (replacementHtml || "").trim();
  const startBlock = closestBlock(startPos.node, container);
  const endBlock = closestBlock(endPos.node, container);
  const sameBlock = startBlock === endBlock;
  const hasBlockMarkup = /<(p|ul|ol|h[1-6]|blockquote)[\s>]/i.test(replacement);

  // Whole block selected + block-level replacement (e.g. Expand returned
  // several <p>s): swap the block element itself — splicing block tags
  // inside a <p> would nest invalid HTML.
  if (sameBlock && startBlock !== container && hasBlockMarkup && squash(startBlock.textContent) === target) {
    startBlock.parentNode.replaceChild(
      document.createRange().createContextualFragment(replacement),
      startBlock
    );
    return cleanupHtml(container);
  }

  // If the AI wrapped a short replacement in a single <p> but the selection
  // sits inside an existing block, unwrap it so we don't nest <p> in <p>.
  const singleP = replacement.match(/^<p[^>]*>([\s\S]*)<\/p>$/i);
  if (singleP && sameBlock && !/<p[\s>]/i.test(singleP[1])) {
    replacement = singleP[1];
  }

  range.deleteContents();
  range.insertNode(range.createContextualFragment(replacement));

  return cleanupHtml(container);
};

// Deleting a range can leave behind emptied inline tags (<strong></strong>)
// or empty paragraphs at the splice boundaries — drop them.
const cleanupHtml = (container) => {
  container.querySelectorAll("strong, em, b, i, u, a, span").forEach((el) => {
    if (!el.textContent.trim() && !el.querySelector("img, br")) el.remove();
  });
  container.querySelectorAll("p, li").forEach((el) => {
    if (!el.textContent.trim() && !el.querySelector("img, br")) el.remove();
  });
  return container.innerHTML;
};

const closestBlock = (node, container) => {
  let el = node.parentNode;
  while (el && el !== container) {
    if (BLOCK_TAGS.test(el.nodeName)) return el;
    el = el.parentNode;
  }
  return container;
};

// Wraps each run of inline/text content under `root` in its own clone of
// the highlight span, recursing into block-level children (h1-h6, p, li…)
// instead of wrapping them directly. A <span> is inline (phrasing) content
// — putting a block element like <h1> inside one is invalid HTML, and even
// though a live DOM operation (as opposed to innerHTML parsing) doesn't
// get corrected by the browser, it still breaks the visual background
// across the block's own line box, so the highlight silently rendered
// with no visible color at the exact cross-boundary case this fixes
// (selecting from inside a heading through to the following paragraph).
// Multiple spans sharing the same data-comment-id is fine — the click
// handler (handleContentClick) looks up the comment by that id regardless
// of how many highlight spans carry it.
const wrapInlineRuns = (root, makeSpan) => {
  const children = Array.from(root.childNodes);
  let run = [];
  const flushRun = () => {
    if (!run.length) return;
    const span = makeSpan();
    root.insertBefore(span, run[0]);
    run.forEach((n) => span.appendChild(n));
    run = [];
  };
  children.forEach((child) => {
    if (child.nodeType === 1 && BLOCK_TAGS.test(child.nodeName)) {
      flushRun();
      wrapInlineRuns(child, makeSpan);
    } else {
      run.push(child);
    }
  });
  flushRun();
};

/**
 * Select (highlight) the given plain text inside `element` and scroll it into
 * view — lets the user see exactly which sentence a readability issue refers
 * to without modifying the document. Returns false when not found.
 */
export const selectTextInElement = (element, text) => {
  const target = squash(text);
  if (!element || !target) return false;

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
  const map = [];
  let squashed = "";
  let node;
  while ((node = walker.nextNode())) {
    const value = node.nodeValue || "";
    for (let i = 0; i < value.length; i++) {
      if (!/\s/.test(value[i])) {
        squashed += value[i];
        map.push({ node, offset: i });
      }
    }
  }

  let idx = squashed.indexOf(target);
  if (idx === -1) idx = squashed.toLowerCase().indexOf(target.toLowerCase());
  if (idx === -1) return false;

  const startPos = map[idx];
  const endPos = map[idx + target.length - 1];
  if (!startPos || !endPos) return false;

  const range = document.createRange();
  range.setStart(startPos.node, startPos.offset);
  range.setEnd(endPos.node, endPos.offset + 1);

  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
  const anchorEl = startPos.node.parentElement;
  if (anchorEl && anchorEl.scrollIntoView) {
    anchorEl.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  return true;
};

/** True when the current window selection lives inside `element`. */
export const selectionInsideElement = (element) => {
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0 || !element) return false;
  return element.contains(sel.anchorNode) && element.contains(sel.focusNode);
};

// ── English variant (US / UK) ───────────────────────────────────────────────

export const englishVariantNote = (variant) =>
  variant === "uk"
    ? `LANGUAGE VARIANT — UK ENGLISH (hard requirement): use British spelling, grammar and punctuation in every word you write: colour, organise, analyse, centre, programme, travelling, behaviour, licence (noun), "-ise" endings. Never use American spellings.`
    : `LANGUAGE VARIANT — US ENGLISH (hard requirement): use American spelling, grammar and punctuation in every word you write: color, organize, analyze, center, program, traveling, behavior, license, "-ize" endings. Never use British spellings.`;

// Instructions prepended to any editing action that targets a selection, so
// the model edits ONLY the excerpt and returns it in a shape that can be
// spliced back into the surrounding document.
export const EXCERPT_RULES = `IMPORTANT — SELECTED EXCERPT ONLY:
The text below is an excerpt the user selected inside a larger post. The rest of the post is NOT shown and must NOT be rewritten, summarised, or referenced.
- Return ONLY the edited version of this excerpt — no surrounding content, no preamble.
- Match the input's shape: if the input is a bare phrase or sentence with no <p> tags, return a bare phrase or sentence with no <p> wrapper; if it has paragraphs, keep the same paragraph structure.
- Keep any HTML tags in the input (<strong>, <a>, <em>) attached to the same words unless the task requires changing them.
- Scale the task to the excerpt's size (expanding one sentence means 2-4 sentences, not new full paragraphs).
- The result must read naturally in place of the original excerpt.

`;

// ── Inline readability highlighting (originality.ai-style RAG marks) ─────────
// Non-destructive: highlights are rendered into the editor's DOM only. They
// carry the class prefix "wa-hl" and are stripped before the content value is
// ever read/saved (see stripHighlights + the DescriptionEditor input handler),
// so the stored post stays clean HTML.

const HL_STYLES = {
  "word-high":     "background:#fee2e2;border-bottom:2px solid #ef4444;border-radius:2px;cursor:help;",
  "word-medium":   "background:#fef3c7;border-bottom:2px solid #f59e0b;border-radius:2px;cursor:help;",
  "word-low":      "background:#dcfce7;border-bottom:2px solid #22c55e;border-radius:2px;cursor:help;",
  "sentence-high": "background:rgba(239,68,68,0.12);border-radius:3px;",
  "sentence-medium": "background:rgba(251,191,36,0.14);border-radius:3px;",
  "sentence-low":  "background:rgba(34,197,94,0.12);border-radius:3px;",
};

const esc = (s = "") =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const escAttr = (s = "") => esc(s).replace(/"/g, "&quot;");

// Wrap flagged words inside a plain-text run; everything is HTML-escaped.
const highlightWords = (text, wordMap) => {
  if (!wordMap || !Object.keys(wordMap).length) return esc(text);
  let out = "", last = 0, m;
  const re = /[A-Za-z'’]+/g;
  while ((m = re.exec(text))) {
    const key = m[0].toLowerCase().replace(/[^a-z]/g, "");
    const info = wordMap[key];
    if (!info) continue;
    out += esc(text.slice(last, m.index));
    out += info.reason
      ? `<span class="wa-hl-word wa-hl-${info.severity}" style="${HL_STYLES["word-" + info.severity]}" title="${escAttr(info.reason)}">${esc(m[0])}</span>`
      : `<span class="wa-hl-word wa-hl-${info.severity}" style="${HL_STYLES["word-" + info.severity]}">${esc(m[0])}</span>`;
    last = m.index + m[0].length;
  }
  return out + esc(text.slice(last));
};

// Non-whitespace char → original index map, for locating a squashed sentence
// inside a single text-node string.
const squashMap = (text) => {
  let squashed = "";
  const map = [];
  for (let i = 0; i < text.length; i++) {
    if (!/\s/.test(text[i])) { squashed += text[i]; map.push(i); }
  }
  return { squashed, map };
};

const highlightTextNode = (text, wordMap, sentences) => {
  const { squashed, map } = squashMap(text);
  const lower = squashed.toLowerCase();
  const ranges = [];
  (sentences || []).forEach((s) => {
    const target = squash(s.text);
    if (!target) return;
    let idx = squashed.indexOf(target);
    if (idx === -1) idx = lower.indexOf(target.toLowerCase());
    if (idx === -1) return;
    const start = map[idx];
    const end = map[idx + target.length - 1] + 1;
    if (start != null && end != null) ranges.push({ start, end, sev: s.severity, tip: s.tip });
  });
  ranges.sort((a, b) => a.start - b.start);

  let out = "", cursor = 0;
  ranges.forEach((r) => {
    if (r.start < cursor) return; // drop overlaps
    out += highlightWords(text.slice(cursor, r.start), wordMap);
    const sev = ["high", "medium", "low"].includes(r.sev) ? r.sev : "medium";
    out += r.tip
      ? `<span class="wa-hl-sentence wa-hl-s-${sev}" style="${HL_STYLES["sentence-" + sev]}" title="${escAttr(r.tip)}">`
      : `<span class="wa-hl-sentence wa-hl-s-${sev}" style="${HL_STYLES["sentence-" + sev]}">`;
    out += highlightWords(text.slice(r.start, r.end), wordMap);
    out += "</span>";
    cursor = r.end;
  });
  return out + highlightWords(text.slice(cursor), wordMap);
};

/**
 * Return an HTML string with complex words and hard sentences wrapped in
 * tooltip highlight spans. `wordMap` = { word: {severity, reason} };
 * `sentences` = [{ text, severity, tip }]. Returns the original html unchanged
 * when there's nothing to flag.
 */
export const buildHighlightedHtml = (html, wordMap, sentences) => {
  if (!html) return html;
  const hasWords = wordMap && Object.keys(wordMap).length;
  const hasSentences = sentences && sentences.length;
  if (!hasWords && !hasSentences) return html;

  const container = document.createElement("div");
  container.innerHTML = html;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  const nodes = [];
  let node;
  while ((node = walker.nextNode())) nodes.push(node);

  nodes.forEach((n) => {
    const text = n.nodeValue || "";
    if (!text.trim()) return;
    const replaced = highlightTextNode(text, wordMap, sentences);
    if (replaced !== esc(text)) {
      const frag = document.createRange().createContextualFragment(replaced);
      n.parentNode.replaceChild(frag, n);
    }
  });
  return container.innerHTML;
};

// ── Blog Write review comments (BlogWriteVersionEditor.js) ─────────────────
// A different flavor of highlight from wa-hl above: these wrap a reviewer's
// own selected text (not an AI flag) and carry a data-comment-id so a click
// can look the comment back up. Same "re-locate the plain-text anchor via
// TreeWalker + squash" technique as the rest of this file, not reusing
// buildHighlightedHtml directly since that function's shape (wordMap +
// severity/tip sentences) is specific to the readability-flagging feature.

/**
 * Locate the DOM position of a selection made inside `container` and return
 * which occurrence (0-based) of `targetText` — squashed the same way
 * buildCommentHighlights matches — that selection falls on. Needed because
 * the same sentence can legitimately appear more than once in a document
 * (a repeated subheader/paragraph, for instance): matching by text alone
 * always resolves to the first occurrence, so a highlight for the SECOND
 * one would incorrectly land on the first. Call this at comment-creation
 * time (the DOM still reflects exactly what the user selected) and persist
 * the result as the comment's anchorIndex.
 */
export const findOccurrenceIndex = (container, range, targetText) => {
  const target = squash(targetText || "");
  if (!target || !container || !range) return 0;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  let squashed = "";
  let selectionOffset = -1;
  let node;
  while ((node = walker.nextNode())) {
    const value = node.nodeValue || "";
    if (node === range.startContainer && range.startOffset <= value.length) {
      selectionOffset = squashed.length + squash(value.slice(0, range.startOffset)).length;
    }
    squashed += squash(value);
  }
  if (selectionOffset === -1) return 0;

  let count = -1;
  let searchFrom = 0;
  let idx = squashed.indexOf(target, searchFrom);
  while (idx !== -1 && idx <= selectionOffset) {
    count++;
    searchFrom = idx + 1;
    idx = squashed.indexOf(target, searchFrom);
  }
  return Math.max(0, count);
};

/**
 * Wrap each comment's `anchorText` — at its `anchorIndex`-th matching
 * location (0-based; defaults to 0, i.e. the first match, for comments
 * written before anchorIndex existed) — inside `html` with a clickable
 * highlight span carrying `data-comment-id`.
 * `comments` = [{ _id, anchorText, anchorIndex }, ...]. A comment whose
 * anchorText can no longer be found (e.g. the source file changed), or
 * whose anchorIndex now exceeds how many times it appears, falls back to
 * the first match rather than dropping the highlight entirely. Non-
 * destructive to comments themselves; only shapes this render.
 */
export const buildCommentHighlights = (html, comments) => {
  if (!html || !comments || !comments.length) return html;
  const container = document.createElement("div");
  container.innerHTML = html;

  comments.forEach((c) => {
    const target = squash(c.anchorText || "");
    if (!target) return;

    // Re-walk fresh for every comment since each successful wrap changes
    // the DOM (a wrapped run's text node splits), invalidating any map
    // built before it.
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    const map = [];
    let squashed = "";
    let node;
    while ((node = walker.nextNode())) {
      // Don't match inside a highlight span already placed for an earlier
      // comment — avoids nesting one highlight inside another.
      if (node.parentElement && node.parentElement.closest(".bwv-comment-hl")) continue;
      const value = node.nodeValue || "";
      for (let i = 0; i < value.length; i++) {
        if (!/\s/.test(value[i])) {
          squashed += value[i];
          map.push({ node, offset: i });
        }
      }
    }

    const wantIndex = c.anchorIndex || 0;
    let idx = -1;
    let count = -1;
    let searchFrom = 0;
    let found = squashed.indexOf(target, searchFrom);
    while (found !== -1) {
      count++;
      idx = found;
      if (count === wantIndex) break;
      searchFrom = found + 1;
      found = squashed.indexOf(target, searchFrom);
    }
    // wantIndex not reached (content changed since the comment was made,
    // or a case-only difference) — fall back to whatever we found, same
    // as the old always-first-match behavior, rather than losing the
    // highlight entirely.
    if (idx === -1) idx = squashed.toLowerCase().indexOf(target.toLowerCase());
    if (idx === -1) return;

    const startPos = map[idx];
    const endPos = map[idx + target.length - 1];
    if (!startPos || !endPos) return;

    const range = document.createRange();
    range.setStart(startPos.node, startPos.offset);
    range.setEnd(endPos.node, endPos.offset + 1);

    const makeSpan = () => {
      const span = document.createElement("span");
      span.className = "bwv-comment-hl";
      span.setAttribute("data-comment-id", c._id);
      span.style.cssText = "background:#fef08a;border-bottom:2px solid #eab308;border-radius:2px;cursor:pointer;";
      return span;
    };
    try {
      // surroundContents() throws whenever the range's start/end sit at
      // different depths — e.g. a selection starting inside a <strong>
      // heading and ending in a plain sibling paragraph (the exact "select
      // from a bold heading through to the end" report). That's not a rare
      // edge case here, it's the common one: a reviewer highlighting a
      // sentence that starts right after a bold label. extractContents()
      // has no such restriction, but naively wrapping its whole result in
      // one <span> is invalid whenever a block element (h1, li, p…) ends up
      // inside it — a <span> is inline content, so the highlight's
      // background silently fails to render across the block's line box.
      // wrapInlineRuns wraps only the inline runs and recurses into block
      // children instead of wrapping them, so every block keeps its own
      // highlight span(s) sharing the same data-comment-id.
      const fragment = range.extractContents();
      const scratch = document.createElement("div");
      scratch.appendChild(fragment);
      wrapInlineRuns(scratch, makeSpan);
      const toInsert = document.createDocumentFragment();
      while (scratch.firstChild) toInsert.appendChild(scratch.firstChild);
      range.insertNode(toInsert);
    } catch (e) {
      // Still guard the rare case extractContents itself can't handle
      // (e.g. a range boundary inside a non-splittable node) — skip this
      // one rather than risk corrupting the DOM.
    }
  });

  return container.innerHTML;
};

/** Remove any wa-hl highlight spans, unwrapping their text. Safe on clean HTML. */
export const stripHighlights = (html) => {
  if (!html || html.indexOf("wa-hl") === -1) return html;
  const c = document.createElement("div");
  c.innerHTML = html;
  c.querySelectorAll("span[class*='wa-hl']").forEach((el) => {
    const parent = el.parentNode;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  });
  c.normalize();
  return c.innerHTML;
};
